import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from "@/components/ui/Button/Button";
import { InputForm } from "@/components/ui/Input/InputForm";
import { Pages } from "@/utils/routes.utils";
import { useResponsive } from "@/utils/useResponsive"
import { FormEvent, Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthStore } from '@/store/auth/auth.store';
import { useVerifyAccount } from '@/store/auth/auth.service';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getCustomComponent } from '@/utils/custom-components.utils';
import { useBrandConfig } from '@/utils/useBrandConfig';

export const Signin = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { email, setEmail, loading, setLoading } = AuthStore();
  const { name } = useBrandConfig();

  const { mutate: verifyAccount } = useVerifyAccount({
    onSuccess: () => {
      setEmail(formValues.email);
      navigate(Pages.PIN);
      setLoading(false);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const {
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm({
    resolver: zodResolver(z.object({
      email: z.string().email(t("inputs.email.error")),
    })),
    mode: 'onChange',
    defaultValues: {
      email: email ?? '',
    },
  })

  const handleNext = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trigger();
    if (isValid) {
      setLoading(true);
      verifyAccount(formValues.email);
    }
  }

  const formValues = watch();

  useEffect(() => {
    if (formValues.email) {
      trigger('email');
    }
  }, [formValues.email, trigger])

  const CustomLogo = getCustomComponent({
    brandName: name,
    folder: 'BrandLogo',
  })

  const Content = (
    <form
      onSubmit={handleNext}
      className={`py-7 flex h-full flex-col justify-start items-center ${isMobile ? 'px-7' : 'w-[360px]'}`}>
      <div className='flex flex-col flex-1 justify-between items-center w-full'>
        <Suspense fallback={<div className="h-[42px]"></div>}>
          <CustomLogo />
        </Suspense>
        <div className={`flex flex-col justify-center items-center w-full ${isMobile ? '' : 'pt-[50px]'}`}>
          <div className="text-headingXLarge mb-2 text-center">{t("auth.signin.title")}</div>
          <div className="opacity-50 text-center mb-8 leading-5">
            {t("auth.signin.subtitle")}
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col justify-start items-center w-full">
        <InputForm
          required
          placeholder={t("inputs.email.placeholder")}
          className="mt-3"
          type="email"
          value={formValues.email}
          handleChange={(value) => setValue('email', value)}
          error={errors.email?.message}
        />
        <div className="py-6">
          <Button
            label={t("buttons.next")}
            primary
            variant="Rounded"
            onClick={() => { }}
            type="submit"
            size="small"
            loading={loading}
          />
        </div>
        <div className="flex flex-row justify-center items-center gap-1 leading-none p-0 m-0">
          <div className="text-bodySmall opacity-70">{t("auth.signin.footer")}</div>
          <button onClick={() => navigate(Pages.SIGNUP)}>
            <div className="text-labelSmall underline">{t("auth.signin.signup")}</div>
          </button>
        </div>
      </div>
    </form>
  );

  return isMobile ? Content : (
    <div className="flex justify-center items-start pt-[100px]">
      {Content}
    </div>
  );
}